import axios from 'axios'

export interface GstValidationResult {
  isValid: boolean
  status: string
  legalName?: string
  address?: string
  pincode?: string
  cancellationDate?: string
  message: string
}

export const gstApi = {
  validateGst: async (gstin: string): Promise<GstValidationResult> => {
    const trimmed = gstin.trim().toUpperCase()
    // GST Identification Number format check
    const formatRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i
    if (!formatRegex.test(trimmed)) {
      return {
        isValid: false,
        status: 'Invalid Format',
        message: 'Invalid GST Number.',
      }
    }

    try {
      const apiBase = import.meta.env.VITE_VERIFY_GST_URL
      const secretKey = import.meta.env.VITE_VERIFY_GST_SECRET_KEY

      const response = await axios.post(
        `${apiBase}/validate-business-registration/gst`,
        { gstin: trimmed },
        {
          headers: {
            'secretkey': secretKey,
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        }
      )

      const responseData = response.data
      const gstinData = responseData?.data?.gstin_data || responseData?.data || responseData

      const status = gstinData?.status || responseData?.status
      const legalName = gstinData?.legal_name || gstinData?.trade_name || responseData?.legal_name
      const address = gstinData?.principal_address?.address || gstinData?.address || responseData?.address
      const cancellationDate = gstinData?.date_of_cancellation || gstinData?.cancellation_date || responseData?.date_of_cancellation

      // Extract 6-digit pincode from the address string
      let pincode: string | undefined = undefined
      if (address) {
        const pinMatch = address.match(/\b\d{6}\b/)
        if (pinMatch) {
          pincode = pinMatch[0]
        }
      }

      if (!status) {
        return {
          isValid: false,
          status: 'Unknown',
          message: 'Invalid GST Number.',
        }
      }

      const isActive = status.toLowerCase() === 'active'

      if (isActive) {
        return {
          isValid: true,
          status,
          legalName,
          address,
          pincode,
          message: 'GST verified successfully.',
        }
      } else {
        return {
          isValid: false,
          status,
          cancellationDate,
          message: 'GST is not active.',
        }
      }
    } catch (error: any) {
      return {
        isValid: false,
        status: 'Verification Failed',
        message: 'Invalid GST Number.',
      }
    }
  },
}
